// Pure builders + validation for chat navigation intents. Kept free of
// React / store / pi-ai imports so the branch matrix is unit-testable
// (navIntent.test.js) and so a hallucinated scope from the agent can't
// strand the user on an empty view — a bad intent fails closed with a
// message the agent can act on, rather than opening a dead tab.

import { isNavigableScopeDim, navigableScopeDims } from "./analyticsClient.js";
import { GRAPH_CLAIM_PARAM, GRAPH_FOCUS_PARAM } from "./traceGraphOverlay.js";
import { TURN_FOCUS_PARAM } from "./traceTurnFocus.js";

// Studio detail views the agent may open (parallels selectStudioItem in
// App.jsx). Signals/triggers are intentionally excluded from v1.
export const STUDIO_VIEWS = ["kernels", "canvases", "workflows"];

// Normalize a single scope step's ids: accept an `ids` array or a lone
// `id`, keep only non-empty strings. Returns [] when nothing usable.
function stepIds(step) {
  if (Array.isArray(step?.ids)) {
    return step.ids.filter((x) => typeof x === "string" && x.length > 0);
  }
  if (typeof step?.id === "string" && step.id.length > 0) return [step.id];
  return [];
}

// Validate a scope path the agent wants to open in Explore.
// Returns { ok: true, scopePath: [{dim, ids}] } or { ok: false, error }.
// Every dim must be server-navigable (a real filter), and every step
// must carry at least one id — the ids come from the agent's own
// analytics_query results grouped by that dim.
export function validateExploreScope(scopePath) {
  if (!Array.isArray(scopePath) || scopePath.length === 0) {
    return { ok: false, error: "scopePath must be a non-empty array of {dim, ids} steps." };
  }
  const clean = [];
  for (const step of scopePath) {
    if (!step || typeof step !== "object") {
      return { ok: false, error: "each scope step must be an object {dim, ids}." };
    }
    const { dim } = step;
    if (!isNavigableScopeDim(dim)) {
      return {
        ok: false,
        error: `dim "${dim}" is not navigable. Use one of: ${navigableScopeDims().join(", ")}.`,
      };
    }
    const ids = stepIds(step);
    if (ids.length === 0) {
      return {
        ok: false,
        error: `scope step "${dim}" needs at least one id — pass ids you got from an analytics_query grouped by ${dim} (do not invent ids).`,
      };
    }
    clean.push({ dim, ids });
  }
  return { ok: true, scopePath: clean };
}

// Validate a Studio item target (kernel / canvas / workflow + its id).
// Returns { ok: true, view, itemId } or { ok: false, error }.
export function validateStudioTarget(view, itemId) {
  if (!STUDIO_VIEWS.includes(view)) {
    return { ok: false, error: `view must be one of: ${STUDIO_VIEWS.join(", ")}.` };
  }
  if (typeof itemId !== "string" || itemId.length === 0) {
    return { ok: false, error: `a ${view} id is required (the kernel command / canvas slug / workflow command).` };
  }
  return { ok: true, view, itemId };
}

// Hybrid-autonomy decision: should a navigation be APPLIED directly
// (vs surfaced as a one-click offer)? Direct only when the user
// explicitly asked AND the chat is docked beside the main column — a
// floating overlay always offers so it never yanks the page out from
// behind itself.
export function shouldNavigateDirectly(requested, docked) {
  return Boolean(requested) && Boolean(docked);
}

// Existence-gate outcome. A navigation may proceed only on a decisive
// "present" status — "active" (a scope probe found data) or "exists" (a
// studio item is saved). Everything else blocks: "empty"/"missing"
// (decisively absent) and "unknown" (probe error / can't determine). The
// gate fails closed, so an unrecognized status blocks too.
export function navStatusBlocks(status) {
  return status !== "active" && status !== "exists";
}

// Human label for a scope path, for the confirmation/affordance copy.
// e.g. [{dim:"endpoints", ids:["a","b"]}] → "2 endpoints".
export function describeScope(scopePath) {
  if (!Array.isArray(scopePath) || scopePath.length === 0) return "Explore";
  return scopePath
    .map((s) => {
      const n = Array.isArray(s.ids) ? s.ids.length : 0;
      return n === 1 ? `1 ${s.dim.replace(/s$/, "")}` : `${n} ${s.dim}`;
    })
    .join(" › ");
}

// The URL a chat's trace navigation writes (App's openTraceExternal): Explore,
// the reading to open on, the chat whose knowledge graph the readings wear and
// the claim to arrive on. A turn carried for the conversation on screen means
// nothing in the next one, so it goes; so do the selections of the modes the
// navigation leaves. `expand` opens the trace's panel full screen
// (enterDetailFullScreen), unless another column already holds the screen.
export function applyTraceNavParams(next, { reading = "lanes", graph = null, claim = null, expand = false } = {}) {
    next.set("mode", "explore");
    for (const key of ["menu", "kernel", "canvas", TURN_FOCUS_PARAM]) next.delete(key);
    if (reading && reading !== "turns") next.set("hist", reading);
    else next.delete("hist");
    if (graph) next.set(GRAPH_FOCUS_PARAM, graph);
    else next.delete(GRAPH_FOCUS_PARAM);
    if (graph && claim) next.set(GRAPH_CLAIM_PARAM, claim);
    else next.delete(GRAPH_CLAIM_PARAM);
    if (expand && (!next.get("xcol") || next.get("xcol") === "detail")) next.set("xcol", "detail");
    return next;
}
